import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { ReactThreeFiber, useLoader } from '@react-three/fiber'
import {  useThree, useFrame } from '@react-three/fiber'
import LSystem  from './LSystem';
import { typeImplementation } from '@testing-library/user-event/dist/type/typeImplementation';


function Particles({ count, radius }) {
    const mesh = useRef();
    const { scene, camera } = useThree();
    const texture = useLoader(THREE.TextureLoader, "cloud.png");
    let lastTime = 0;
    let lastImage = '';
  
    const dummy = useMemo(() => new THREE.Object3D(), []);

    useEffect(() => camera.position.set(-2,5,0));
    
    const particles = useMemo(() => {
      const temp = [];
      for (let i = 0; i < count; i++) {
        const endLife = Math.random() / 2;
        const life = 0;
        const phi = Math.random() * Math.PI * 2;
        const theta = Math.random() * Math.PI * 2;
        const start = new THREE.Vector3(
            Math.sin(phi) * Math.cos(theta) * radius,
            Math.cos(phi) * Math.cos(theta) * radius,
            Math.sin(theta) * radius
        );
        
      //   const start = new THREE.Vector3(
      //     Math.random() * radius * 5,
      //     -1 * radius,
      //     Math.random() * radius * 5
      // ).add(camera.position);
        // const pos = points[i];

        temp.push({ start, life, endLife });
      }
      return temp
    }, [count])

    
    useFrame(({clock, raycaster}) => {
      if(!mesh.current) return;
      
      // Go through each particle
      particles.forEach((particle, i) => {
        
        let { start } = particle;
        // Change position
        let lerping = start.clone();
        lerping.lerp(camera.position, particle.life);
        dummy.position.copy(lerping);
        dummy.scale.set(1,1,1);
        dummy.lookAt(camera.position);
        // dummy.lookAt(new THREE.Vector3(0,(i%5)*4,0));
        dummy.updateMatrix();
        // And apply the matrix to the instanced mesh
        mesh?.current.setMatrixAt(i, dummy.matrix);

        
        particle.life += 0.1 * (clock.elapsedTime - lastTime);
        // console.log(particle.life)
        if(particle.life >= particle.endLife) {
          particle.endLife = Math.random() / 2;
          particle.life = 0;
          const phi = Math.random() * Math.PI * 2;
          const theta = Math.random() * Math.PI * 2;
          particle.start = new THREE.Vector3(
              Math.sin(phi) * Math.cos(theta) * radius,
              Math.cos(phi) * Math.cos(theta) * radius,
              Math.sin(theta) * radius
          ).add(camera.position);
        //   particle.start = new THREE.Vector3(
        //     (Math.random()-0.5) * radius * 5,
        //     - 1 * radius,
        //     (Math.random()-0.5) * radius * 5
        // ).add(camera.position);
        } 
      })
      mesh.current.instanceMatrix.needsUpdate = true;

      lastTime = clock.elapsedTime;
    })
    return (
      <>
        <instancedMesh ref={mesh} geometry={new THREE.PlaneGeometry(50,50)} args={[null, null, count]} frustumCulled={false} renderOrder={0}>
          <meshPhongMaterial attach="material" map={texture} transparent={true} color={0x505050} opacity={0.5} blending={THREE.AdditiveBlending} depthWrite={false} depthTest={false} />
        </instancedMesh>
      </>
    )
  }


function Planets({ count, radius }) {
    const mesh = useRef();
    const { scene, camera } = useThree();
    const texture = useLoader(THREE.TextureLoader, "cloud.png");
    let lastTime = 0;
    let lastImage = '';
  
    const dummy = useMemo(() => new THREE.Object3D(), []);

    useEffect(() => camera.position.set(-2,5,0));
    
    const particles = useMemo(() => {
      const temp = [];
      for (let i = 0; i < count; i++) {
        const endLife = Math.random();
        const life = 0;
        const rotation = new THREE.Vector3(Math.PI * 2 * Math.random(), Math.PI * 2 * Math.random(), Math.PI * 2 * Math.random());
        
        const start = new THREE.Vector3(
          (Math.random()-0.5) * radius * 5,
          (Math.random()-0.5) * radius * 5,
          (Math.random()-0.5) * radius * 5
        ).add(camera.position);

        const next = new THREE.Vector3(
          (Math.random()-0.5) * radius * 5,
          (Math.random()-0.5) * radius * 5,
          (Math.random()-0.5) * radius * 5
        ).add(camera.position);
        // const pos = points[i];

        temp.push({ start, next, life, endLife, rotation });
      }
      return temp
    }, [count])

    
    useFrame(({clock, raycaster}) => {
      if(!mesh.current) return;
      
      // Go through each particle
      particles.forEach((particle, i) => {
        
        let { start, next, rotation } = particle;
        // Change position
        let lerping = start.clone();
        lerping.lerp(next, particle.life );
        dummy.position.copy(lerping);
        dummy.rotation.set(rotation.x, rotation.y, rotation.z);
        dummy.scale.set(1,1,1);
        // dummy.lookAt(new THREE.Vector3(0,(i%5)*4,0));
        dummy.updateMatrix();
        // And apply the matrix to the instanced mesh
        mesh?.current.setMatrixAt(i, dummy.matrix);
        // mesh?.current.setColorAt(i, new THREE.Color( "hsl(" + Math.floor(Math.random() * 255) + ", 80%, 10%)" ));

        
        particle.life += 0.1 * (clock.elapsedTime - lastTime);
        // console.log(particle.life)
        if(particle.life >= 1) {
          particle.endLife = Math.random();
          particle.life = 0;
          particle.start.copy(particle.next);
          particle.rotation = new THREE.Vector3(Math.PI * 2 * Math.random(), Math.PI * 2 * Math.random(), Math.PI * 2 * Math.random());
        
          particle.next = new THREE.Vector3(
            (Math.random()-0.5) * radius * 5,
            (Math.random()-0.5) * radius * 5,
            (Math.random()-0.5) * radius * 5
          ).add(camera.position);
        } 
      })
      mesh.current.instanceMatrix.needsUpdate = true;

      lastTime = clock.elapsedTime;
    })
    return (
      <>
        <instancedMesh ref={mesh} geometry={new THREE.BoxGeometry(10, 10, 10)} args={[null, null, count]}>
          <meshStandardMaterial attach="material" />
        </instancedMesh>
      </>
    )
  }

let adding = true;
function Stairs({ count, radius, setId }) {
  const mesh = useRef();
  const { scene, camera } = useThree();
  const gltf = useLoader(GLTFLoader, './stairs2.glb');
  const texture = useLoader(THREE.TextureLoader, "noise.png");
  const texture2 = useLoader(THREE.TextureLoader, "matrix.png");
  texture2.wrapS = THREE.RepeatWrapping;
  texture2.wrapT = THREE.RepeatWrapping;
  texture2.repeat.set(2,2)

  const dummy = useMemo(() => new THREE.Object3D(), []);

  
  const particles = useMemo(() => {
    const temp = [];
    let last = new THREE.Vector3(1, 1, 1);
    let turtle = new THREE.Object3D();
    let bCounter = 0;
    turtle.position.set(0,0,0);
    
    const system = new LSystem();
    system.rules = [[/[S]/gm, ['RGGS']], [/[R]/gm, ['/', '\\']], [/[G]/gm, ['^FB^FB', '^FB^FB', '&FB&FB']]];
    system.nextStep(200);

    for (let i = 0; bCounter < count; i++) {

      if(system.string[i] === 'F') {
        let dir = new THREE.Vector3();
        turtle.getWorldDirection(dir);

        turtle.position.add(dir.multiplyScalar(30));
        // console.log(turtle.rotation)
        turtle.updateMatrix();
        
      }
      else if(system.string[i] === '/') {
        turtle.rotateY(Math.PI / 2);
      }
      else if(system.string[i] === '\\') {
        turtle.rotateY(-Math.PI / 2);
      }
      else if(system.string[i] === '^') {
        turtle.position.add(new THREE.Vector3(0,5,0));
      }
      else if(system.string[i] === '&') {
        turtle.position.add(new THREE.Vector3(0,-5,0));
      }
      // else if(system.string[i] === '+') {
      //   turtle.rotateZ(Math.PI / 2);
      // }
      // else if(system.string[i] === '-') {
      //   turtle.rotateZ(-Math.PI / 2);
      // }
      else if(system.string[i] === 'B') {
        bCounter++;
        const start = turtle.position.clone();

        temp.push({ start, rotation: turtle.rotation.clone() });
      }

      // if(i % 100 === 0) {
      //   let box = new THREE.BoxGeometry(2,40,20);
      //   let portal = new THREE.Mesh(box, new THREE.MeshStandardMaterial({color: 'red'}));
      //   portal.position.copy(turtle.position.clone().add(new THREE.Vector3(0,20,0)));
      //   portals.add(portal);
      // }

    }
    
    return temp
  }, [count])

  useEffect(() => {
    if(!adding) return;
    adding = false;

    let paths = [[],[],[]];

    for(let i=0; i < particles.length; i++) {
      for(let j=0; j < 3; j++)
        paths[j].push(particles[i].start.clone().add(new THREE.Vector3(Math.random() * 10 - 5, Math.random() * 10 - 5, Math.random() * 10 - 5)));
    }

    for(let j=0; j < 3; j++) {
      let curve = new THREE.CatmullRomCurve3(paths[j]);
      const geometry = new THREE.TubeGeometry( curve, 1000, 1, 8, false );
      const material = new THREE.MeshBasicMaterial( { map: texture, color: 'green', transparent: true, opacity: 0.5 } );
      const meshNew = new THREE.Mesh( geometry, material );
      mesh.current.add(meshNew);
    }
  })

  
  useFrame(({clock, raycaster}) => {
    if(!mesh.current) return;
    
    if(camera.position.y < -200) {
      setId(1);
    }
    if(camera.position.y > 500) {
      setId(2);
    }
    // Go through each particle
    particles.forEach((particle, i) => {
      
      let { start, rotation } = particle;
      // Change position
      dummy.position.copy(start);
      // dummy.scale.copy(particle.size);
      dummy.scale.copy(new THREE.Vector3(1,1,1));
      dummy.rotation.copy(particle.rotation);
      
      let vec = new THREE.Vector3();
      vec.setFromMatrixColumn(camera.matrix, 0);
      vec = vec.crossVectors(camera.up, vec);
      // vec.y += 0.5;
      vec.multiplyScalar(-200);

      // dummy.lookAt(camera.position.clone().add(vec));
      dummy.updateMatrix();
      // And apply the matrix to the instanced mesh
      mesh.current?.setMatrixAt(i, dummy.matrix);
      // mesh?.current.setColorAt(i, new THREE.Color( "hsl(" + Math.floor(Math.random() * 255) + ", 80%, 10%)" ));

      
    })
    if(mesh.current)
    mesh.current.instanceMatrix.needsUpdate = true;

  })
  return (
    <>
      <instancedMesh ref={mesh} geometry={new THREE.BoxGeometry(30,5,30)} args={[null, null, count]} renderOrder={-1}>
        <meshStandardMaterial attach="material" side={THREE.DoubleSide} map={texture2} transparent={true}/>
      </instancedMesh>
    </>
  )
}


function Buildings({ count, offset, setId }) {
  const mesh = useRef();
  const { scene, camera } = useThree();

  const dummy = useMemo(() => new THREE.Object3D(), []);
  const gltf = useLoader(GLTFLoader, './block.glb');
  console.log(gltf.scene)
  let startTime = 0;
  let pos = new THREE.Vector3(offset.x*50,0,offset.y*50);
  let lastPos = 0;
  
  const particles = useMemo(() => {
    const temp = [];

    const rules=[];
    
    for(let i=0; i < 10; i++) {
      rules[i] = Math.floor(Math.random() * 2);
    }
    console.log(rules)

    const mat = [];
    for(let i=0; i < 10; i++) {
      mat.push([]);
      for(let j=0; j < 10; j++) {
        mat[i][j] = Math.floor(Math.random() * 2);
      }
    }

    for(let floor = 0; floor < 100; floor++) {
      for (let i = 1; i < 9; i++) {
        for (let j = 1; j < 9; j++) {
          let sum = mat[i-1][j-1] + mat[i-1][j] + mat[i-1][j+1] + 
                    mat[i][j-1] + mat[i][j] + mat[i][j+1] + 
                    mat[i+1][j-1] + mat[i+1][j] + mat[i+1][j+1];

          mat[i][j] = rules[sum];
          if(rules[sum] === 1)
            temp.push({start: new THREE.Vector3(i*5,floor*5,j*5).add(pos)});
        }
      }
    }
    while(temp.length < count) temp.push({start: new THREE.Vector3(0,10000,0)});
    
    return temp
  }, [count])

  useEffect(() => {
    camera.position.set(25,10,0);
    camera.lookAt(0,0,0);
  })
  
  useFrame(({clock, raycaster}) => {
    if(!mesh.current) return;
    
    if(camera.position.y < -50) {
      setId(0);
    }
    
    // Go through each particle
    particles.forEach((particle, i) => {
      
      let { start } = particle;
      // Change position
      dummy.position.copy(start);
      dummy.position.y = (new THREE.Vector3(0,-20,0)).lerp(start, Math.min((clock.elapsedTime - startTime) / 10, 1)).y;
      // dummy.position.add(new THREE.Vector3(offset.x * 100, 0, offset.y * 100));
      // dummy.scale.copy(particle.size);
      dummy.scale.copy(new THREE.Vector3(0.6,6,0.6));
      
      // let vec = new THREE.Vector3();
      // vec.setFromMatrixColumn(camera.matrix, 0);
      // vec = vec.crossVectors(camera.up, vec);
      // vec.multiplyScalar(-200);
      // // vec.y = i*5 - camera.position.y;

      // dummy.lookAt(camera.position.clone().add(vec));
      dummy.updateMatrix();
      // And apply the matrix to the instanced mesh
      mesh.current?.setMatrixAt(i, dummy.matrix);
      // mesh?.current.setColorAt(i, new THREE.Color( "hsl(" + Math.floor(Math.random() * 255) + ", 80%, 10%)" ));

      
    })
    if(mesh.current)
    mesh.current.instanceMatrix.needsUpdate = true;

    let gap = 100;

    if(Math.abs(pos.x - camera.position.x) > 100 || Math.abs(pos.z - camera.position.z) > 100) {
      let vector = new THREE.Vector3( 0, 0, -1 );
      vector.applyQuaternion( camera.quaternion );

      // pos.copy(camera.position.clone().add(vector.multiplyScalar(100)));
      // pos.copy(camera.position.clone());
      if(Math.abs(pos.x - camera.position.x) > 100)
        pos.x -= Math.sign(pos.x - camera.position.x) * 200;
      if(Math.abs(pos.z - camera.position.z) > 100)
        pos.z -= Math.sign(pos.z - camera.position.z) * 200;
      pos.y=0;
      console.log(pos)

      startTime = clock.elapsedTime;
      
      const rules=[];
      
      for(let i=0; i < 10; i++) {
        rules[i] = Math.floor(Math.random() * 2);
      }

      const mat = [];
      for(let i=0; i < 10; i++) {
        mat.push([]);
        for(let j=0; j < 10; j++) {
          mat[i][j] = Math.floor(Math.random() * 2);
        }
      }
      mat[5][5] = 1;

      // console.log(particles.length)
      let temp = 0;
      for(let floor = 0; floor < 120; floor++) {
        for (let i = 1; i < 9; i++) {
          for (let j = 1; j < 9; j++) {
            let sum = mat[i-1][j-1] + mat[i-1][j] + mat[i-1][j+1] + 
                      mat[i][j-1] + mat[i][j] + mat[i][j+1] + 
                      mat[i+1][j-1] + mat[i+1][j] + mat[i+1][j+1];

            mat[i][j] = rules[sum];
            if(rules[sum] === 1 && temp < particles.length) {
              particles[temp].start = new THREE.Vector3(i*5,floor*5,j*5).add(pos);
              temp++;
            }
          }
        }
      }
      // console.log(temp)
    }
  })
  return (
    <>
      <instancedMesh ref={mesh} geometry={gltf.scene.children[0].geometry} args={[null, null, count]} renderOrder={-1}>
        <meshStandardMaterial attach="material" color={0x505050} />
      </instancedMesh>
    </>
  )
}


function Road({ count }) {
  const mesh = useRef();
  const { scene, camera } = useThree();
  let lastTime = 0;

  const dummy = useMemo(() => new THREE.Object3D(), []);

  useEffect(() => camera.position.set(-2,5,0));
  
  const particles = useMemo(() => {
    const temp = [];
    for (let i = 0; i < count / 2; i++) {
      
      let start = new THREE.Vector3((i - count / 4) * 100 + 25, 0, 0);
      let rotation = new THREE.Vector3(0, Math.PI / 2, 0);

      temp.push({ start, rotation });

      
      start = new THREE.Vector3(0, 0, (i - count / 4) * 100 + 25);
      rotation = new THREE.Vector3(0, 0, 0);

      temp.push({ start, rotation });
    }
    return temp
  }, [count])

  
  useFrame(({clock, raycaster}) => {
    if(!mesh.current) return;
    
    // Go through each particle
    particles.forEach((particle, i) => {
      
      let { start,  rotation } = particle;
      // Change position
      
      if(Math.abs(start.x - camera.position.x) > 1000 || Math.abs(start.z - camera.position.z) > 1000) {
        particle.start.x -= Math.sign(start.x - camera.position.x) * 2000;
        particle.start.z -= Math.sign(start.z - camera.position.z) * 2000;
      }
      
      dummy.position.copy(start);
      dummy.rotation.set(rotation.x, rotation.y, rotation.z);
      dummy.updateMatrix();
      // And apply the matrix to the instanced mesh
      mesh?.current.setMatrixAt(i, dummy.matrix);
      // mesh?.current.setColorAt(i, new THREE.Color( "hsl(" + Math.floor(Math.random() * 255) + ", 80%, 10%)" ));

    })
    mesh.current.instanceMatrix.needsUpdate = true;

    lastTime = clock.elapsedTime;
  })
  return (
    <>
      <instancedMesh ref={mesh} geometry={new THREE.BoxGeometry(10000, 1, 25)} args={[null, null, count]}>
        <meshBasicMaterial attach="material" color={0xaaaaaa}/>
      </instancedMesh>
    </>
  )
}

export { Particles, Planets, Stairs, Buildings, Road };